import { useMemo, useRef, useLayoutEffect, useState, useCallback } from 'react';
import styles from './FieldDiaryCalendar.module.css';
import type { FieldDiaryEntry } from '../../types';
import type { CalendarDay } from '../../utils/calendar.utils';

const WEEKDAYS = ['DOM', 'SEG', 'TER', 'QUA', 'QUI', 'SEX', 'SAB'];

const STATUS_LABEL_CLASS: Record<string, string> = {
	pending: styles.labelPending,
	completed: styles.labelDone,
	upcoming: styles.labelUpcoming,
	note: styles.labelNote,
};

const MIN_COL_REM = 4;

export type { CalendarDay };

export interface FieldDiaryCalendarProps {
	farmName: string;
	calendarDays: CalendarDay[];
	entriesByDay: Record<number, FieldDiaryEntry[]>;
	selectedDay: number | null;
	onSelectDay: (day: number | null) => void;
	isToday: (day: number) => boolean;
	onEntryClick?: (entry: FieldDiaryEntry) => void;
}

export const FieldDiaryCalendar = ({
	farmName,
	calendarDays,
	entriesByDay,
	selectedDay,
	onSelectDay,
	isToday,
	onEntryClick,
}: FieldDiaryCalendarProps) => {
	const gridRef = useRef<HTMLDivElement>(null);
	const wrapperRef = useRef<HTMLDivElement>(null);
	const [colWidths, setColWidths] = useState<string>('repeat(7, 1fr)');
	const [needsScroll, setNeedsScroll] = useState(false);

	const selectedColIndex = useMemo(() => {
		if (selectedDay === null) return null;
		for (let i = 0; i < calendarDays.length; i++) {
			const { day, isCurrentMonth } = calendarDays[i];
			if (isCurrentMonth && day === selectedDay) return i % 7;
		}
		return null;
	}, [selectedDay, calendarDays]);

	const computeColumns = useCallback(() => {
		if (!gridRef.current) return;

		const rootFontSize = Number.parseFloat(getComputedStyle(document.documentElement).fontSize);
		const minPx = MIN_COL_REM * rootFontSize;
		const minWithDataPx = 8 * rootFontSize;
		const maxPx = 13 * rootFontSize;
		const containerWidth =
			wrapperRef.current?.offsetWidth ??
			gridRef.current?.parentElement?.offsetWidth ??
			gridRef.current?.offsetWidth ??
			0;

		const cells = gridRef.current.querySelectorAll<HTMLElement>('[data-col]');

		const naturalByCol: number[] = Array(7).fill(0);

		const prevGridCols = gridRef.current.style.gridTemplateColumns;
		gridRef.current.style.gridTemplateColumns = 'repeat(7, max-content)';

		cells.forEach((cell) => {
			const col = Number.parseInt(cell.dataset.col ?? '0', 10);
			const natural = cell.scrollWidth;
			if (natural > naturalByCol[col]) naturalByCol[col] = natural;
		});

		gridRef.current.style.gridTemplateColumns = prevGridCols;

		const hasData = naturalByCol.map((w) => w > minPx);

		const weights = naturalByCol.map((w, i) => {
			if (!hasData[i]) return minPx;
			return Math.min(Math.max(w, minWithDataPx), maxPx);
		});

		const totalWeight = weights.reduce((a, b) => a + b, 0);

		const getExpandedFr = (width: number) => {
			if (width >= 1280) return 2;
			if (width >= 1024) return 3;
			if (width >= 768) return 4;
			return 7;
		};

		if (selectedColIndex !== null) {
			const expandedFr = getExpandedFr(containerWidth);
			const otherFr = 1;
			const totalFr = expandedFr + otherFr * 6;
			const eachOtherPx = (otherFr / totalFr) * containerWidth;
			const expandedPx = (expandedFr / totalFr) * containerWidth;
			const needsOverflow = naturalByCol.some((_, i) => {
				const min = hasData[i] ? minWithDataPx : minPx;
				return (i === selectedColIndex ? expandedPx : eachOtherPx) < min;
			});
			if (needsOverflow) {
				const parts = naturalByCol.map((_, i) => {
					const min = hasData[i] ? minWithDataPx : minPx;
					if (i === selectedColIndex) return `${Math.max(expandedPx, min)}px`;
					return `${Math.max(eachOtherPx, min)}px`;
				});
				setNeedsScroll(true);
				setColWidths(parts.join(' '));
			} else {
				const parts = Array(7).fill(`${otherFr}fr`);
				parts[selectedColIndex] = `${expandedFr}fr`;
				setNeedsScroll(false);
				setColWidths(parts.join(' '));
			}
			return;
		}

		const scaledPx = weights.map((w) => (w / totalWeight) * containerWidth);

		const clamped = scaledPx.map((w, i) => Math.max(w, hasData[i] ? minWithDataPx : minPx));
		const totalClamped = clamped.reduce((a, b) => a + b, 0);

		if (totalClamped > containerWidth) {
			setNeedsScroll(true);
			setColWidths(clamped.map((w) => `${w}px`).join(' '));
		} else {
			setNeedsScroll(false);
			const frs = weights.map((w) => w / totalWeight);
			setColWidths(frs.map((fr) => `${fr}fr`).join(' '));
		}
	}, [selectedColIndex]);

	useLayoutEffect(() => {
		computeColumns();
	}, [calendarDays, entriesByDay, computeColumns]);

	useLayoutEffect(() => {
		computeColumns();
	}, [selectedColIndex, computeColumns]);

	useLayoutEffect(() => {
		const el = wrapperRef.current;
		if (!el) return;
		const ro = new ResizeObserver(() => computeColumns());
		ro.observe(el);
		return () => ro.disconnect();
	}, [computeColumns]);

	const handleDayClick = (day: number, isCurrentMonth: boolean) => {
		if (!isCurrentMonth) return;
		onSelectDay(selectedDay === day ? null : day);
	};

	return (
		<div ref={wrapperRef} className={styles.card}>
			<div className={styles.header}>
				<span className={styles.title}>Calendário - {farmName}</span>
				<div className={styles.legend}>
					<span className={styles.legendItem}>
						<span className={`${styles.dot} ${styles.dotPending}`} />
						Pendente
					</span>
					<span className={styles.legendItem}>
						<span className={`${styles.dot} ${styles.dotDone}`} />
						Realizada
					</span>
					<span className={styles.legendItem}>
						<span className={`${styles.dot} ${styles.dotUpcoming}`} />
						A vencer
					</span>
					<span className={styles.legendItem}>
						<span className={`${styles.dot} ${styles.dotNote}`} />
						Nota
					</span>
				</div>
			</div>

			<div
				className={styles.calendarScrollWrapper}
				style={{ overflowX: needsScroll ? 'auto' : 'visible' }}
			>
				<div
					ref={gridRef}
					className={styles.calendarGrid}
					style={
						{
							'--fd-col-widths': colWidths,
						} as React.CSSProperties
					}
				>
					{WEEKDAYS.map((wd, i) => (
						<span key={wd} data-col={i} className={styles.weekday}>
							{wd}
						</span>
					))}

					{calendarDays.map(({ day, isCurrentMonth }, idx) => {
						const colIndex = idx % 7;
						const dayEntries = isCurrentMonth ? (entriesByDay[day] ?? []) : [];
						const isSelected = isCurrentMonth && day === selectedDay;
						const isTodayDay = isCurrentMonth && isToday(day);

						return (
							<button
								key={idx}
								type="button"
								data-col={colIndex}
								disabled={!isCurrentMonth}
								onClick={() => handleDayClick(day, isCurrentMonth)}
								className={[
									styles.dayCell,
									isCurrentMonth ? '' : styles.dayCellOther,
									isSelected ? styles.dayCellSelected : '',
								]
									.filter(Boolean)
									.join(' ')}
							>
								<span
									className={[
										styles.dayNumber,
										isCurrentMonth ? styles.dayNumberCurrent : styles.dayNumberOther,
										isTodayDay ? styles.dayNumberToday : '',
									]
										.filter(Boolean)
										.join(' ')}
								>
									{day}
								</span>
								{isCurrentMonth && dayEntries.length > 0 && (
									<div className={styles.labels}>
										{dayEntries.map((entry) => (
											<span
												key={entry._id}
												className={[
													styles.label,
													STATUS_LABEL_CLASS[entry.status],
													colIndex === selectedColIndex ? styles.labelExpanded : '',
													onEntryClick ? styles.labelClickable : '',
												]
													.filter(Boolean)
													.join(' ')}
												onClick={
													onEntryClick
														? (e) => {
																e.stopPropagation();
																onEntryClick(entry);
															}
														: undefined
												}
											>
												{entry.title}
											</span>
										))}
									</div>
								)}
							</button>
						);
					})}
				</div>
			</div>
		</div>
	);
};
